import React, { useState, useEffect } from 'react';
import { Key, CheckCircle, XCircle } from 'lucide-react';
import type { Permission } from '../../services/security/permissions.service';
import { ConfirmationToast } from '../ui/ConfirmationToast';
import { usePermissions } from '../../hooks/usePermissions';

interface PermissionDetailsDrawerProps {
  isOpen: boolean;
  permission: Permission | null;
  onClose: () => void;
  onEditPermission?: (permission: Permission) => void;
  onTogglePermissionStatus?: (permission: Permission) => Promise<void>;
  onDeletePermission?: (permission: Permission) => Promise<void>;
}

export const PermissionDetailsDrawer: React.FC<PermissionDetailsDrawerProps> = ({
  isOpen,
  permission,
  onClose,
  onEditPermission,
  onTogglePermissionStatus,
  onDeletePermission
}) => {
  const { hasPermission } = usePermissions();
  const [isVisible, setIsVisible] = useState(false);
  const [currentPermission, setCurrentPermission] = useState<Permission | null>(null);
  
  // Estados para confirmación y loading
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [confirmationAction, setConfirmationAction] = useState<'toggle' | 'delete' | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  // Verificar permisos
  const canEditPermissions = hasPermission('Permisos', 'editar');
  const canDeletePermissions = hasPermission('Permisos', 'eliminar');
  const canAdministerPermissions = hasPermission('Permisos', 'administrar');
  const canToggleStatus = canAdministerPermissions;

  useEffect(() => {
    if (isOpen && permission) {
      setCurrentPermission(permission);
      const timer = setTimeout(() => {
        setIsVisible(true);
      }, 10);
      return () => clearTimeout(timer);
    } else if (!isOpen) {
      setIsVisible(false);
    }
  }, [isOpen, permission]);

  useEffect(() => {
    if (!isVisible && !isOpen) {
      const timer = setTimeout(() => {
        setCurrentPermission(null);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [isVisible, isOpen]);

  const handleClose = () => {
    setIsVisible(false);
    onClose();
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (activo: boolean) => {
    return activo 
      ? 'bg-green-100 text-green-800' 
      : 'bg-red-100 text-red-800';
  };

  const handleEditPermission = () => {
    if (currentPermission && onEditPermission) {
      onEditPermission(currentPermission);
    }
  };

  const handleTogglePermissionStatus = () => {
    setConfirmationAction('toggle');
    setShowConfirmation(true);
  };

  const handleDeletePermission = () => {
    setConfirmationAction('delete');
    setShowConfirmation(true);
  };

  const handleConfirmAction = async () => {
    if (!currentPermission || !confirmationAction) return;
    
    setIsLoading(true);
    try {
      if (confirmationAction === 'toggle' && onTogglePermissionStatus) {
        await onTogglePermissionStatus(currentPermission);
      } else if (confirmationAction === 'delete' && onDeletePermission) {
        await onDeletePermission(currentPermission);
        handleClose();
        return;
      }
    } catch (error) {
      console.error('Error al ejecutar acción:', error);
    } finally {
      setIsLoading(false);
      setShowConfirmation(false);
      setConfirmationAction(null);
    }
  };

  const handleCancelAction = () => {
    setShowConfirmation(false);
    setConfirmationAction(null);
  };

  const getConfirmationConfig = () => {
    if (!currentPermission || !confirmationAction) return null;
    
    if (confirmationAction === 'toggle') {
      return {
        title: currentPermission.activo ? 'Desactivar Permiso' : 'Activar Permiso',
        message: currentPermission.activo 
          ? `¿Estás seguro de que deseas desactivar el permiso "${currentPermission.nombre}"? Este permiso dejará de estar disponible para asignar a módulos.`
          : `¿Estás seguro de que deseas activar el permiso "${currentPermission.nombre}"? Este permiso estará disponible para asignar a módulos.`,
        confirmText: currentPermission.activo ? 'Desactivar' : 'Activar',
        type: currentPermission.activo ? 'danger' as const : 'success' as const
      };
    }

    if (confirmationAction === 'delete') {
      return {
        title: 'Eliminar Permiso',
        message: `¿Estás seguro de que deseas eliminar el permiso "${currentPermission.nombre}"? Esta acción no se puede deshacer y el permiso será removido de todos los módulos que lo tengan asignado.`,
        confirmText: 'Eliminar',
        type: 'danger' as const
      };
    }
    
    return null;
  };

  if (!isOpen && !currentPermission) return null;
  if (!currentPermission) return null;

  return (
    <div className="fixed left-0 right-0 bottom-0 top-0 z-[60] overflow-hidden">
      <div className="absolute inset-0 overflow-hidden">
        <div 
          className="absolute inset-0 cursor-pointer"
          onClick={handleClose}
          aria-label="Cerrar drawer"
        />
        
        <section className={`absolute right-0 top-0 h-full w-full max-w-4xl border-l border-gray-200 transform transition-transform duration-300 ease-in-out ${
          isVisible ? 'translate-x-0' : 'translate-x-full'
        }`}>
          <div 
            className="h-full flex flex-col bg-white shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="px-6 py-4 bg-gray-100 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h2 className="text-xs font-medium text-gray-900">
                  Detalles del Permiso
                </h2>
                <button
                  onClick={handleClose}
                  className="text-gray-500 hover:text-gray-700 transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            
            {/* Content */}
            <div className="flex-1 overflow-y-auto">
              {/* Header del permiso */}
              <div className="text-center py-6 bg-gradient-to-b from-gray-50 to-white">
                <div className="mx-auto h-20 w-20 rounded-full bg-gradient-to-r from-blue-400 to-indigo-500 flex items-center justify-center mb-3 shadow-lg">
                  <Key className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xs font-medium text-gray-900 mb-1">
                  {currentPermission.nombre}
                </h3>
                {currentPermission.descripcion && (
                  <p className="text-gray-600 mb-2">{currentPermission.descripcion}</p>
                )}
                <div className="flex justify-center">
                  <span className={`inline-flex items-center px-3 py-1 text-xs font-medium rounded-full ${getStatusColor(currentPermission.activo)}`}>
                    {currentPermission.activo ? (
                      <>
                        <CheckCircle className="w-3 h-3 mr-1" />
                        Activo
                      </>
                    ) : (
                      <>
                        <XCircle className="w-3 h-3 mr-1" />
                        Inactivo
                      </>
                    )}
                  </span>
                </div>
              </div>

              {/* Contenido */}
              <div className="px-6 py-6">
                <div>
                  {/* Información básica */}
                  <div className="mb-6">
                    <h4 className="text-xs font-medium text-gray-900 mb-4 border-b border-gray-200 pb-2">
                      Información Básica
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-medium text-gray-500">Nombre del Permiso</label>
                        <p className="text-gray-900 font-medium">{currentPermission.nombre}</p>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-500">Estado</label>
                        <div className="flex items-center space-x-2">
                          <div className={`w-3 h-3 rounded-full ${currentPermission.activo ? 'bg-green-500' : 'bg-red-500'}`}></div>
                          <p className="text-gray-900 font-medium">{currentPermission.activo ? 'Activo' : 'Inactivo'}</p>
                        </div>
                      </div>
                      {currentPermission.descripcion && (
                        <div className="md:col-span-2">
                          <label className="block text-xs font-medium text-gray-500">Descripción</label>
                          <p className="text-gray-900">{currentPermission.descripcion}</p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Información del Sistema */}
                  <div className="pt-4 border-t border-gray-200">
                    <h4 className="text-xs font-medium text-gray-600 mb-3">
                      Información del Sistema
                    </h4>
                    <div className="space-y-2 text-xs text-gray-500 bg-gray-50 p-3 rounded-lg">
                      <div><span className="font-medium">ID:</span> {currentPermission.id}</div>
                      <div><span className="font-medium">Creado:</span> {formatDate(currentPermission.creadoEn)}</div>
                      <div><span className="font-medium">Última actualización:</span> {formatDate(currentPermission.actualizadoEn)}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer con acciones */}
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
              <div className="flex space-x-3">
                {canEditPermissions && (
                  <button 
                    onClick={handleEditPermission}
                    className="flex-1 bg-blue-600 text-white px-3 py-2 text-xs rounded-lg hover:bg-blue-700 transition-colors font-medium"
                  >
                    Editar Permiso
                  </button>
                )}
                {canToggleStatus && (
                  <button 
                    onClick={handleTogglePermissionStatus}
                    disabled={isLoading}
                    className={`flex-1 px-3 py-2 text-xs rounded-lg border transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed ${
                      currentPermission.activo 
                        ? 'bg-orange-50 text-orange-700 border-orange-200 hover:bg-orange-100' 
                        : 'bg-green-50 text-green-700 border-green-200 hover:bg-green-100'
                    }`}
                  >
                    {currentPermission.activo ? 'Desactivar' : 'Activar'}
                  </button>
                )}
                {canDeletePermissions && (
                  <button 
                    onClick={handleDeletePermission}
                    className="flex-1 bg-red-50 text-red-700 border border-red-200 px-3 py-2 text-xs rounded-lg hover:bg-red-100 transition-colors font-medium"
                  >
                    Eliminar
                  </button>
                )}
              </div>
            </div>
          </div>
        </section>
      </div>
      
      {/* Confirmation Toast */}
      {(() => {
        const config = getConfirmationConfig();
        return config ? (
          <ConfirmationToast
            isOpen={showConfirmation}
            title={config.title}
            message={config.message}
            confirmText={config.confirmText}
            type={config.type}
            onConfirm={handleConfirmAction}
            onCancel={handleCancelAction}
            isLoading={isLoading}
          />
        ) : null;
      })()}
    </div>
  );
};